import { shallowEqual, useSelector } from 'react-redux';
import type { Table } from 'schema-dts';
import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';
import DocumentSchema from '@/components/DocumentSchema';
import { getDividendSectionSelector } from '@/pages/CompanyReport/components/Dividend/redux/selectors';
import { DividendYieldTable, HeaderDataTable } from './styled';
import { useSummaryV2 } from '@/hooks/useCompany';
import { CompanyReportTableRow } from '@/pages/CompanyReport/styled';

interface ReduxState {
  company: CompanyState;
}

function selector(state: ReduxState) {
  const dividend = getDividendSectionSelector(state, {});

  return {
    companyName: dividend.companyName,
    exchangeCountryISO: dividend.exchangeCountryISO,
    industryName: dividend.industryName,
    dividendYieldFormatted: dividend.dividendYieldFormatted,
    marketDividendYieldP25Formatted: dividend.marketDividendYieldP25Formatted,
    marketDividendYieldP75Formatted: dividend.marketDividendYieldP75Formatted,
    industryDividendYieldFormatted: dividend.industryDividendYieldFormatted,
    dividendYieldFuture3YFormatted: dividend.dividendYieldFuture3YFormatted,
  };
}

export const DividendYieldVsMarketTable = () => {
  const {
    companyName,
    exchangeCountryISO,
    industryName,
    dividendYieldFormatted,
    marketDividendYieldP25Formatted,
    marketDividendYieldP75Formatted,
    industryDividendYieldFormatted,
    dividendYieldFuture3YFormatted,
  } = useSelector<Parameters<typeof selector>[0], ReturnType<typeof selector>>(
    selector,
    shallowEqual
  );
  const { tickerSymbol } = useSummaryV2();
  const companyLabel = tickerSymbol || companyName || '';
  const header = `${companyName} Dividend Yield vs Market`;
  const caption = `How does ${
    tickerSymbol || companyName
  } dividend yield compare to the market?`;

  return (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: header,
          about: caption,
        }}
      />
      <HeaderDataTable>
        <thead>
          <tr>
            <th colSpan={2}>{header}</th>
          </tr>
        </thead>
      </HeaderDataTable>
      <DividendYieldTable
        columnTextAlignments="l"
        data-cy-id="dividend-yield-vs-market-table"
      >
        <caption data-cy-id="dividend-yield-vs-market-table-caption">
          {caption}
        </caption>
        <thead>
          <tr>
            <th>Segment</th>
            <th>Dividend Yield</th>
          </tr>
        </thead>
        <tbody>
          <CompanyReportTableRow
            selected
            data-cy-id="dividend-yield-vs-market-table-row-0"
          >
            <td>{`Company (${companyLabel})`}</td>
            <td>{dividendYieldFormatted}</td>
          </CompanyReportTableRow>
          <tr data-cy-id="dividend-yield-vs-market-table-row-1">
            <td>{`Market Bottom 25% (${exchangeCountryISO})`}</td>
            <td>{marketDividendYieldP25Formatted}</td>
          </tr>
          <tr data-cy-id="dividend-yield-vs-market-table-row-2">
            <td>{`Market Top 25% (${exchangeCountryISO})`}</td>
            <td>{marketDividendYieldP75Formatted}</td>
          </tr>
          <tr data-cy-id="dividend-yield-vs-market-table-row-3">
            <td>{`Industry Average (${industryName})`}</td>
            <td>{industryDividendYieldFormatted}</td>
          </tr>
          <tr data-cy-id="dividend-yield-vs-market-table-row-4">
            <td>{`Analyst forecast in 3 Years (${companyLabel})`}</td>
            <td>{dividendYieldFuture3YFormatted}</td>
          </tr>
        </tbody>
      </DividendYieldTable>
    </>
  );
};
